'use client';
import React, { useState } from 'react';

export default function LecturesSection() {
  const [activeMedia, setActiveMedia] = useState<'youtube' | 'spotify'>('youtube');

  return (
    <>
      <style>{`
        .lectures-wrap {
          background: #f8f7f4;
        }
        .lectures-media-tabs {
          display: flex;
          gap: 0;
          margin-bottom: 28px;
          border: 1px solid rgba(59,130,246,0.15);
          border-radius: 8px;
          overflow: hidden;
          width: fit-content;
        }
        .lectures-media-tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 22px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.04em;
          background: none;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
          color: rgba(30,58,138,0.5);
        }
        .lectures-media-tab.active {
          background: #1e3a8a;
          color: #fff;
        }
        .lectures-media-tab:hover:not(.active) {
          background: rgba(59,130,246,0.06);
          color: #1e3a8a;
        }

        .lectures-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 28px;
        }
        .lectures-embed-card {
          background: #fff;
          border: 1px solid rgba(59,130,246,0.1);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 12px rgba(30,58,138,0.06);
        }
        .lectures-embed-header {
          padding: 16px 20px 12px;
          border-bottom: 1px solid rgba(59,130,246,0.08);
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .lectures-embed-icon {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          flex-shrink: 0;
        }
        .lectures-embed-icon.yt { background: rgba(255,0,0,0.08); }
        .lectures-embed-icon.sp { background: rgba(30,215,96,0.1); }
        .lectures-embed-title {
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          font-weight: 600;
          color: #1e3a8a;
          margin: 0;
        }
        .lectures-embed-subtitle {
          font-family: 'DM Sans', sans-serif;
          font-size: 11px;
          color: rgba(30,58,138,0.4);
          margin: 2px 0 0;
        }
        .lectures-embed-body {
          padding: 0;
        }
        .lectures-embed-body iframe {
          display: block;
          width: 100%;
          border: none;
        }

        .lectures-placeholder {
          padding: 40px 24px;
          text-align: center;
        }
        .lectures-placeholder-icon {
          font-size: 36px;
          margin-bottom: 12px;
          display: block;
        }
        .lectures-placeholder-title {
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          font-weight: 600;
          color: #1e3a8a;
          margin: 0 0 6px;
        }
        .lectures-placeholder-text {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          color: rgba(30,58,138,0.5);
          line-height: 1.6;
          margin: 0;
        }

        .lectures-archive-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          color: #1e3a8a;
          text-decoration: none;
          margin-top: 16px;
          padding: 8px 16px;
          border: 1px solid rgba(30,58,138,0.2);
          border-radius: 6px;
          transition: all 0.2s;
        }
        .lectures-archive-link:hover {
          background: rgba(30,58,138,0.05);
          border-color: rgba(30,58,138,0.4);
        }

        @media (max-width: 768px) {
          .lectures-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="lectures-wrap">
        <div className="lectures-grid">
          {/* YouTube — Latest Khutbah */}
          <div className="lectures-embed-card">
            <div className="lectures-embed-header">
              <div className="lectures-embed-icon yt">▶</div>
              <div>
                <p className="lectures-embed-title">Latest Khutbah</p>
                <p className="lectures-embed-subtitle">Jumu&apos;ah · Masjid Al-Ihsan</p>
              </div>
            </div>
            <div className="lectures-embed-body">
              {/* YouTube channel embed — latest video from Masjid Al-Ihsan */}
              <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                <iframe
                  src="https://www.youtube.com/embed?listType=user_uploads&list=masjidihsan&index=1&autoplay=0"
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                  title="Latest Khutbah — Masjid Al-Ihsan"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div style={{ padding: '14px 20px 16px' }}>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: 'rgba(30,58,138,0.5)', lineHeight: 1.6, margin: 0 }}>
                  Watch the latest Friday Khutbah from Imam Abdullah Madyun and the Masjid Al-Ihsan community.
                </p>
                <a
                  href="https://www.youtube.com/@masjidihsan"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lectures-archive-link"
                >
                  View Full Lecture Archive →
                </a>
              </div>
            </div>
          </div>

          {/* Spotify — Latest Talks/Broadcasts */}
          <div className="lectures-embed-card">
            <div className="lectures-embed-header">
              <div className="lectures-embed-icon sp">♪</div>
              <div>
                <p className="lectures-embed-title">Talks &amp; Broadcasts</p>
                <p className="lectures-embed-subtitle">Podcast · Islamic Lectures</p>
              </div>
            </div>
            <div className="lectures-embed-body">
              {/* Spotify podcast embed */}
              <iframe
                src="https://open.spotify.com/embed/show/3qKpBnHFkWFLRPAFJjFLqY?utm_source=generator&theme=0"
                width="100%"
                height="352"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                title="Islamic Lectures — Spotify"
                style={{ borderRadius: '0 0 12px 12px' }}
              />
              <div style={{ padding: '14px 20px 16px' }}>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: 'rgba(30,58,138,0.5)', lineHeight: 1.6, margin: 0 }}>
                  Listen to talks, broadcasts, and Islamic lectures from the Masjid Al-Ihsan community on Spotify.
                </p>
                <a
                  href="https://open.spotify.com/show/3qKpBnHFkWFLRPAFJjFLqY"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lectures-archive-link"
                >
                  Open on Spotify →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
